import BarItem from '@/features/pos/components/ProductCatalog/parts/CategoryBar/parts/BarItem'
import { measureSubcategoriesContainerWidth } from '@/features/pos/components/ProductCatalog/parts/CategoryBar/parts/CategoryWithSubcategories/utils'
import {
    MeasureItemLeftPosition,
    ScrollMeasureHandlers,
    Subcategory as SubcategoryData,
} from '@/features/pos/components/ProductCatalog/parts/CategoryBar/types'
import { FC, memo, useCallback, useRef } from 'react'
import { View } from 'react-native'
import { useDerivedValue } from 'react-native-reanimated'
import { createStyleSheet, useStyles } from 'react-native-unistyles'
import Subcategories from 'src/features/pos/components/ProductCatalog/parts/CategoryBar/parts/CategoryWithSubcategories/parts/Subcategories'

interface CategoryWithSubcategoriesProps {
    selected: boolean
    id: string
    name: string
    horizontal?: boolean
    subcategories: SubcategoryData[]
    selectedSubcategoryId?: string
    onPress: (
        id: string,
        subcategoryId: string | undefined,
        scrollMeasureHandlers: ScrollMeasureHandlers
    ) => void
}

const CategoryWithSubcategories: FC<CategoryWithSubcategoriesProps> = ({
    selected,
    id,
    name,
    horizontal = false,
    subcategories,
    selectedSubcategoryId,
    onPress,
}) => {
    const subcategoriesContainerRef = useRef<View>(null)

    const isExpanded = useDerivedValue(() => selected)

    const { styles } = useStyles(stylesheet, { horizontal })

    const handleCategoryPress = useCallback(
        (id: string, measureItemLeftPosition: MeasureItemLeftPosition) =>
            onPress(id, undefined, {
                measureItemLeftPosition,
                measureSubcategoriesContainerWidth: () =>
                    measureSubcategoriesContainerWidth(subcategoriesContainerRef),
            }),
        [onPress]
    )

    const handleSubcategoriesPress = useCallback(
        (subcategoryId: string, measureItemLeftPosition: MeasureItemLeftPosition) =>
            onPress(id, subcategoryId, {
                measureItemLeftPosition,
                measureSubcategoriesContainerWidth: () =>
                    measureSubcategoriesContainerWidth(subcategoriesContainerRef),
            }),
        [id, onPress]
    )

    return (
        <View style={styles.container}>
            <BarItem selected={selected} id={id} onPress={handleCategoryPress}>
                {name}
            </BarItem>
            <Subcategories
                subcategories={subcategories}
                selectedId={selectedSubcategoryId}
                expanded={isExpanded}
                horizontal={horizontal}
                onPress={handleSubcategoriesPress}
                ref={subcategoriesContainerRef}
            />
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: {
        variants: {
            horizontal: {
                true: {
                    flexDirection: 'row',
                },
                false: {
                    width: '100%',
                },
            },
        },
    },
})

export default memo(CategoryWithSubcategories)
